import { Label } from "@ascleo/ui/components/ui/label";
import { Slider } from "@ascleo/ui/components/ui/slider";
import { FRONTDESK_LANGUAGE_CATALOG } from "../../lib/i18n/language-catalog";
import { Globe, Languages, Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { audioEmitter } from "../../lib/audio";
import { KioskSelectField } from "../../components/kiosk-field";
import { SettingsCard, SettingsSwitchRow } from "./settings-layout";
import type { SettingsFormApi } from "./use-settings-controller";

const LANGUAGE_OPTIONS = FRONTDESK_LANGUAGE_CATALOG.map(({ code, label }) => ({
  value: code,
  label,
}));

/**
 * Renders terminal-local language and speech output settings
 * ("Sprache & Sprachausgabe"): the patients' default language, whether a
 * session starts with the language choice, and the read-aloud volume.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * type LookSettingsSectionProps = Parameters<typeof LookSettingsSection>[0];
 * ```
 */
export function LookSettingsSection({ form }: { form: SettingsFormApi }) {
  const { t } = useTranslation();

  return (
    <SettingsCard
      icon={Languages}
      title={t("settings.language.title")}
      description={t("settings.language.description")}
    >
      <form.Field name="defaultKioskLanguage">
        {(field) => (
          <KioskSelectField
            id="settings-default-kiosk-language"
            testId="frontdesk-settings-default-kiosk-language"
            label={t("settings.appearance.default-kiosk-language")}
            icon={Languages}
            value={field.state.value ?? "de"}
            onValueChange={field.handleChange}
            options={LANGUAGE_OPTIONS}
            description={t("settings.appearance.default-kiosk-language-hint")}
          />
        )}
      </form.Field>

      <form.Field name="languageSelectionOnSessionStart">
        {(field) => (
          <SettingsSwitchRow
            id="frontdesk-settings-language-entry-switch"
            icon={Globe}
            label={t("settings.appearance.language-first")}
            checked={field.state.value ?? false}
            onCheckedChange={field.handleChange}
            testId="frontdesk-settings-language-entry-switch"
          />
        )}
      </form.Field>

      <form.Field name="voiceOverVolume">
        {(field) => (
          <div className="space-y-3 rounded-2xl bg-foreground-soft px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <Label className="flex items-center gap-3 text-base font-semibold">
                <Volume2 aria-hidden className="size-6 text-muted-foreground" />
                {t("settings.appearance.voice-over-volume")}
              </Label>
              <span className="text-lg font-semibold tabular-nums">
                {field.state.value}%
              </span>
            </div>
            <Slider
              data-testid="frontdesk-settings-voice-over-slider"
              value={[field.state.value]}
              onValueChange={(rawValue) => {
                const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
                if (value !== undefined) {
                  field.handleChange(value);
                  audioEmitter.emit("audio:set-volume", value);
                }
              }}
              min={0}
              max={100}
              step={5}
              className="w-full py-4 [&_[data-slot=slider-thumb]]:size-7 [&_[data-slot=slider-track]]:h-2!"
            />
            <p className="text-base text-muted-foreground">
              {t("settings.appearance.voice-over-hint")}
            </p>
          </div>
        )}
      </form.Field>
    </SettingsCard>
  );
}
